import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Mic, ArrowRight, CheckCircle2, ClipboardList, Sparkles, Mail, Briefcase, Users, Send } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth() {
  const redirect = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
}

const FEATURES = [
  { icon: ClipboardList, title: "12-step guest workflow", body: "Pre-show + post-show checklist for every guest. Auto-tracked stage badge, segmented progress bar, nothing falls through the cracks." },
  { icon: Sparkles, title: "AI promo in one click", body: "Claude Sonnet 4.5 reads your guest's LinkedIn About + intake bio and writes title, description, social post, newsletter blurb, and guest swipe copy." },
  { icon: Mail, title: "Editable email templates", body: "Five built-in touchpoints — booking, intake, reminders, pre-show, thank-you — plus create your own. Tokens auto-fill from the guest record." },
  { icon: Briefcase, title: "Sponsors + analytics", body: "Track sponsor deals, payment status, deliverables, and watch your guest pipeline by stage." },
  { icon: Users, title: "Multi-tenant from day one", body: "Each podcast is fully scoped. Add admins, manage tiers, and run the platform yourself." },
  { icon: Send, title: "One-click guest emails", body: "Send any template directly via Resend — or copy-paste with one tap. Post-episode thank-you auto-fills from your assets." },
];

export default function Landing() {
  const [form, setForm] = useState({ name: "", email: "", show_name: "", show_url: "", message: "" });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      await api.post("/access-requests", form);
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit. Try again in a moment.");
    } finally { setSubmitting(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--pgm-cream)" }}>
      {/* Header */}
      <header style={{ padding: "20px 40px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1.5px solid var(--pgm-rule)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 40, height: 40, background: "var(--pgm-magenta)", display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid var(--pgm-wine)" }}>
            <Mic size={20} color="var(--pgm-cream)" />
          </div>
          <div className="pgm-display" style={{ fontSize: 20, lineHeight: 1 }}>ShowRunner HQ</div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={startGoogleAuth} className="pgm-btn pgm-btn-ghost" style={{ padding: "8px 14px", fontSize: 11 }} data-testid="header-google">
            Continue with Google
          </button>
          <a href="#request-access" className="pgm-btn pgm-btn-ghost" style={{ padding: "8px 16px", fontSize: 11 }} data-testid="header-cta-request">Request access</a>
          <button onClick={() => nav("/login")} className="pgm-btn" style={{ padding: "8px 16px", fontSize: 11 }} data-testid="header-login">Sign in</button>
        </div>
      </header>

      {/* Hero */}
      <section className="pgm-reveal" style={{ padding: "80px 40px 60px", maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 60, alignItems: "center" }}>
          <div>
            <div className="pgm-micro" style={{ marginBottom: 14 }}>◆ For podcast + live-stream hosts</div>
            <h1 className="pgm-display" style={{ fontSize: 70, lineHeight: 0.98, letterSpacing: "-0.02em" }}>
              Every guest <span style={{ color: "var(--pgm-magenta)" }}>touchpoint</span>, managed from one dashboard.
            </h1>
            <p style={{ fontSize: 18, marginTop: 22, lineHeight: 1.55, maxWidth: 540, color: "var(--pgm-wine)", opacity: 0.85 }}>
              ShowRunner HQ runs the complete guest workflow — from booking confirmation to post-episode thank-you — with AI-written promo copy, editable email templates, and a 12-step checklist for every guest.
            </p>
            <div style={{ display: "flex", gap: 12, marginTop: 28 }}>
              <a href="#request-access" className="pgm-btn" data-testid="hero-cta-request">
                Request access <ArrowRight size={14} />
              </a>
              <button className="pgm-btn pgm-btn-ghost" onClick={() => nav("/login")} data-testid="hero-cta-login">I have an account</button>
            </div>
          </div>
          <div className="pgm-callout" style={{ background: "var(--pgm-wine)", color: "var(--pgm-cream)", padding: "32px 28px" }}>
            <div className="pgm-micro" style={{ color: "var(--pgm-amber)" }}>◆ Built by a host, for hosts</div>
            <div className="pgm-display" style={{ fontSize: 26, marginTop: 10, color: "var(--pgm-cream)", lineHeight: 1.2 }}>
              "Every guest touchpoint — confirmation, intake form, promo copy, post-episode delivery — managed from one place. Nothing falls through the cracks."
            </div>
            <div style={{ marginTop: 18, fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--pgm-amber)" }}>
              — Kim Speed, The Seen Live
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: "20px 40px 80px", maxWidth: 1200, margin: "0 auto" }}>
        <div className="pgm-divider" style={{ marginBottom: 36 }}>◆</div>
        <div className="pgm-micro" style={{ marginBottom: 6 }}>◆ What's inside</div>
        <h2 className="pgm-display" style={{ fontSize: 46, marginBottom: 36 }}>
          Six things you'll stop doing manually.
        </h2>
        <div className="pgm-reveal-stagger" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 18 }}>
          {FEATURES.map((f) => (
            <div key={f.title} className="pgm-card" style={{ borderLeftColor: f.title.charCodeAt(0) % 2 ? "var(--pgm-olive)" : "var(--pgm-magenta)" }}>
              <div style={{ width: 44, height: 44, background: "var(--pgm-amber)", border: "1.5px solid var(--pgm-wine)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                <f.icon size={20} color="var(--pgm-wine)" />
              </div>
              <h3 className="pgm-display" style={{ fontSize: 22 }}>{f.title}</h3>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: "var(--pgm-wine)", opacity: 0.85, marginTop: 6 }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Request access */}
      <section id="request-access" style={{ background: "var(--pgm-wine)", padding: "70px 40px", color: "var(--pgm-cream)" }}>
        <div style={{ maxWidth: 980, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 60, alignItems: "center" }}>
          <div>
            <div className="pgm-micro" style={{ color: "var(--pgm-amber)" }}>◆ Closed beta</div>
            <h2 className="pgm-display" style={{ fontSize: 46, color: "var(--pgm-cream)", marginTop: 8, lineHeight: 1.05 }}>
              Want in?
            </h2>
            <p style={{ fontSize: 16, lineHeight: 1.55, marginTop: 14, opacity: 0.9 }}>
              We're onboarding hosts one show at a time. Tell us about your podcast and we'll be in touch with access details and your starting tier.
            </p>
            <div style={{ marginTop: 20, fontSize: 12, color: "var(--pgm-amber)", textTransform: "uppercase", letterSpacing: "0.16em" }}>
              ◆ Free during beta · Paid tiers coming
            </div>
          </div>

          {sent ? (
            <div className="pgm-callout" style={{ background: "var(--pgm-olive)", color: "var(--pgm-cream)" }} data-testid="access-success">
              <CheckCircle2 size={20} style={{ verticalAlign: "middle", marginRight: 8 }} />
              <strong>You're on the list.</strong>
              <p style={{ marginTop: 8 }}>We'll review your request and reach out by email shortly. In the meantime, follow Kim on LinkedIn for podcast workflow tips.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="pgm-card" style={{ background: "var(--pgm-paper)", color: "var(--pgm-ink)", borderLeftColor: "var(--pgm-magenta)" }}>
              <div className="pgm-micro" style={{ color: "var(--pgm-magenta)" }}>◆ Request access</div>
              <h3 className="pgm-display" style={{ fontSize: 24, marginBottom: 16 }}>Tell us about your show</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div><label className="pgm-label">Your name *</label><input className="pgm-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="access-name" /></div>
                <div><label className="pgm-label">Email *</label><input className="pgm-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="access-email" /></div>
                <div style={{ gridColumn: "1 / -1" }}><label className="pgm-label">Show name *</label><input className="pgm-input" required value={form.show_name} onChange={(e) => setForm({ ...form, show_name: e.target.value })} data-testid="access-show" /></div>
                <div style={{ gridColumn: "1 / -1" }}><label className="pgm-label">Show URL (optional)</label><input className="pgm-input" value={form.show_url} onChange={(e) => setForm({ ...form, show_url: e.target.value })} placeholder="https://yourpodcast.com" data-testid="access-url" /></div>
                <div style={{ gridColumn: "1 / -1" }}><label className="pgm-label">Anything we should know?</label><textarea className="pgm-textarea" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Format, cadence, current pain points..." data-testid="access-message" /></div>
                {error && <div style={{ gridColumn: "1 / -1" }} className="pgm-callout">{error}</div>}
                <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}>
                  <button className="pgm-btn" type="submit" disabled={submitting} data-testid="access-submit">{submitting ? "Sending..." : "Request access"}</button>
                </div>
              </div>
            </form>
          )}
        </div>
      </section>

      <footer style={{ padding: "30px 40px", borderTop: "1.5px solid var(--pgm-rule)", display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--pgm-wine)", opacity: 0.7, fontSize: 12 }}>
        <div>◆ ShowRunner HQ · Mid-century modern for hosts</div>
        <div><Link to="/login" className="pgm-link">Admin sign in →</Link></div>
      </footer>
    </div>
  );
}
